import { jsPDF } from "jspdf";
import JSZip from "jszip";

import {
  downloadFile,
  wait,
  getImage,
  cleanStringValue,
  getLocalisedValue,
} from "@/app/core-module/util/coreHelper";
import {
  splineRegular,
  splineMedium,
  splineSemiBold,
} from "@/app/core-module/util/spline-font";
import { EnvSubscriberHelper } from "@/app/env_subscriber_helper";
import moment from "moment";

const generateQuizAttemptPdf = ({
  quizAttempts,
  subscriberInfo,
  download = false,
  debug = false,
}) => {
  try {
    if (!quizAttempts?.length) return;
    //region pdf default variables and constants
    const defaultFontSize = 10;
    const lineHeight = Math.floor(defaultFontSize / 2);
    let currentY = lineHeight;
    let pageNumber = 1;
    const pageWidth = 196;
    const pageHeight = 280;
    const center = pageWidth / 2;
    const start = 14;
    const end = pageWidth;
    //endregion

    //region jspdf doc init
    let doc = new jsPDF({
      orientation: "p",
      format: "a4",
      compress: true,
    });

    //region font setup
    doc.addFileToVFS("SplineSans-Regular-normal.ttf", splineRegular);
    doc.addFont(
      "SplineSans-Regular-normal.ttf",
      "SplineSans-Regular",
      "normal"
    );

    doc.addFileToVFS("SplineSans-Medium-normal.ttf", splineMedium);
    doc.addFont("SplineSans-Medium-normal.ttf", "SplineSans-Medium", "normal");

    doc.addFileToVFS("SplineSans-SemiBold-normal.ttf", splineSemiBold);
    doc.addFont(
      "SplineSans-SemiBold-normal.ttf",
      "SplineSans-SemiBold",
      "normal"
    );
    //endregion
    //endregion

    //region utility functions
    const setFontType = (type, weight) => {
      if (type === "semi-bold" || weight === 600) {
        doc.setFont("SplineSans-SemiBold", "normal");
      } else if (type === "medium" || weight === 500) {
        doc.setFont("SplineSans-Medium", "normal");
      } else {
        //type=regular
        //weight=400
        doc.setFont("SplineSans-Regular", "normal");
      }
    };

    const trimText = (value, maxLength, maxLines) => {
      return doc.splitTextToSize(value, maxLength).slice(0, maxLines);
    };

    const getImageInfo = (image, width, height, { fit } = {}) => {
      height = height ?? width;
      width = width ?? height;
      let imageInfo, imageHeight, imageWidth, imageAspectRatio;

      if (fit != null && image != null) {
        imageInfo = doc.getImageProperties(image);
        imageHeight = imageInfo?.height ?? height;
        imageWidth = imageInfo?.width ?? width;
        imageAspectRatio = imageHeight / imageWidth;

        if (fit === "WIDTH" && width != null) {
          height = width * imageAspectRatio;
        } else if (fit === "HEIGHT" && height != null) {
          width = height / imageAspectRatio;
        }
      }

      return {
        width,
        height,
        widthOg: imageWidth,
        heightOg: imageHeight,
        aspectRatio: imageAspectRatio,
        fileType: imageInfo?.fileType?.toLowerCase() ?? "jpeg",
      };
    };

    const addImage = (image, x, y, width, height, { fit } = {}) => {
      const imageInfo = getImageInfo(image, width, height, { fit });

      if (image != null) {
        doc.addImage(
          image,
          imageInfo.fileType,
          x,
          y,
          imageInfo.width,
          imageInfo.height
        );
      }

      return { x, y, ...imageInfo };
    };
    //endregion

    const printQuizAttempt = (quizAttempt, date) => {
      currentY = lineHeight;

      printHeader(quizAttempt);
      printHeaderBlock();
      printInfo(quizAttempt);

      gotoNextLine(1.5);
      doc.rect(start, currentY, pageWidth - 14, 4, "F");
      gotoNextLine(2.5);
      doc.setFontSize(10);
      setFontType("normal");

      const questionAnswers = quizAttempt?.questionAnswers ?? [];

      for (let index = 0; index < questionAnswers.length; index++) {
        printRow(questionAnswers[index], index);
      }

      printPageFooter();
    };

    const printHeader = val => {
      addImage(
        getImage(subscriberInfo?.user?.avatar, {
          params: `?r=${Math.random()}`,
        }),
        start,
        currentY - 2,
        45,
        20,
        { fit: "HEIGHT" }
      );
      doc.setFontSize(12);
      setFontType("medium");
      doc.setTextColor(40, 17, 102);

      /*      let headerY = currentY + 5;

      if (val?.attemptedAt) {
        doc.text(
          `Attempt date: ${moment(parseInt(val.attemptedAt)).format(
            "DD MMM YYYY hh:mm A"
          )}`,
          end,
          headerY,
          { align: "right" }
        );
      }*/
    };

    const printHeaderBlock = () => {
      doc.setFillColor(40, 17, 102);
      gotoNextLine(5);
      doc.rect(start, currentY, pageWidth - 14, 13, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      setFontType("semi-bold");
      doc.text("Quiz attempt report".toUpperCase(), center + 10, currentY + 8, {
        align: "center",
      });
    };

    const printInfo = val => {
      doc.setTextColor(40, 17, 102);
      doc.setFontSize(12);
      setFontType("medium");
      gotoNextLine(5);

      const colon1XAxis1 = start + 30;
      const value1XAxis1 = start + 32;
      const title2XAxis1 = start + 100;
      const colon2XAxis1 = start + 123;
      const value2XAxis1 = start + 125;

      const courseTitleSplit = trimText(cleanStringValue(val?.title), 146, 2);
      const courseTitleSplitYAxis =
        courseTitleSplit.length > 1 ? currentY - 4 : currentY;
      doc.text("Quiz Title ", start, courseTitleSplitYAxis);
      doc.text(":", colon1XAxis1, courseTitleSplitYAxis);
      doc.text(courseTitleSplit, value1XAxis1, courseTitleSplitYAxis);

      gotoNextLine(1.5);
      doc.text("Date", start, currentY);
      doc.text(":", colon1XAxis1, currentY);
      doc.text(
        moment(parseInt(val?.attemptedAt)).format("DD MMM YYYY hh:mm A"),
        value1XAxis1,
        currentY
      );

      doc.text("Total mark", title2XAxis1, currentY);
      doc.text(":", colon2XAxis1, currentY);
      doc.text(trimText(`${val?.totalMark}`, 60, 2), value2XAxis1, currentY);

      gotoNextLine(1.5);
      doc.text("Acquired mark", start, currentY);
      doc.text(":", colon1XAxis1, currentY);
      doc.text(trimText(`${val?.acquiredMark}`, 60, 2), value1XAxis1, currentY);

      doc.text("Status", title2XAxis1, currentY);
      doc.text(":", colon2XAxis1, currentY);
      doc.text(trimText(val?.status, 53, 2), value2XAxis1, currentY);
    };

    const printRow = (item, index) => {
      const maxLength = pageWidth - 30;
      const questionStart = start + 8;

      const question = trimText(
        getLocalisedValue(item?.question, { preferredLocale: "en" }),
        maxLength
      );

      if (gotoNextLine(question.length, true) > pageHeight) {
        gotoNextPage();
      }

      doc.text(`${index + 1}.`, start, currentY);
      doc.text(question, questionStart, currentY);
      gotoNextLine(question.length + 1);

      const choices = item?.choices ?? [];

      for (let index = 0; index < choices.length; index++) {
        printSubRow(choices[index], index, {
          givenAnswerKey: item.givenAnswerKey,
          correctAnswerKey: item.correctAnswerKey,
        });
      }

      gotoNextLine(1);
    };

    const printSubRow = (item, index, { givenAnswerKey, correctAnswerKey }) => {
      const maxLength = pageWidth - 50 - 20;
      const choiceStart = start + 10 + 5;

      const choice = trimText(
        getLocalisedValue(item?.value, { preferredLocale: "en" }),
        maxLength
      );

      if (gotoNextLine(choice.length, true) > pageHeight) {
        gotoNextPage();
      }

      if (item.key === correctAnswerKey) {
        doc.setFillColor(0, 255, 0);
      } else if (item.key === givenAnswerKey) {
        doc.setFillColor(255, 0, 0);
      } else {
        doc.setFillColor(255, 255, 255);
      }

      doc.circle(start + 10, currentY - 1.5, 2, "FD");
      doc.text(choice, choiceStart, currentY);
      gotoNextLine(choice.length + 1);
    };

    const printPageFooter = () => {
      // blue color rectangle at the bottom of page
      doc.rect(0, pageHeight + 14, pageWidth + 20, 4, "F");
    };

    const gotoNextLine = (numberOfLines, get) => {
      if (numberOfLines == null) numberOfLines = 1;
      const newCurrentY = currentY + numberOfLines * lineHeight;
      if (get) return newCurrentY;
      currentY = newCurrentY;
      if (currentY > pageHeight) gotoNextPage();
    };

    const gotoNextPage = () => {
      doc.addPage();
      pageNumber++;
      currentY = 2 * lineHeight;
      doc.line(start, pageHeight, end, pageHeight);
      doc.text("Page: " + pageNumber, pageWidth - 14, pageHeight + 5);
      currentY = 3 * lineHeight;
    };

    quizAttempts.forEach((quizAttempt, index) => {
      printQuizAttempt(quizAttempt);

      // if (quizAttempts.length > index + 1) {
      //     doc.addPage();
      //     lineHeight = Math.floor(normalFontSize / 2);
      //     currentY = lineHeight;
      // }
    });

    if (debug) {
      window.open(doc.output("bloburl"), "_blank");
      return true;
    } else {
      if (download) {
        if (quizAttempts.length === 1) {
          return doc.save(`quiz-attempt-0-${Date.now()}`);
        } else {
          return doc.save(`quiz-attempts-${Date.now()}`);
        }
      } else {
        return doc.output("blob");
      }
    }
  } catch (e) {
    console.log("quizPdfHelper.generateQuizAttemptPdf.exception:", e.message);

    return false;
  }
};

export const bulkDownloadQuizAttempts = async ({
  quizAttempts,
  subscriberInfo,
  zip = false,
  debug = false,
}) => {
  try {
    if (!quizAttempts?.length) return;

    await wait(100);

    if (zip && quizAttempts.length > 1) {
      const zip = new JSZip();
      const sheets = zip.folder(`quiz-attempts-${Date.now()}`);

      quizAttempts.forEach((quizAttempt, index) => {
        const blobData = generateQuizAttemptPdf({
          quizAttempts: [quizAttempt],
          subscriberInfo,
        });

        if (blobData) {
          sheets.file(`quiz-attempt-${index}-${Date.now()}.pdf`, blobData);
        }
      });

      if (sheets?.files && Object.keys(sheets?.files).length) {
        return await zip
          .generateAsync({ type: "blob" })
          .then(data => {
            downloadFile({
              data,
              fileName: `quiz-attempts-${Date.now()}.zip`,
            });
            return true;
          })
          .catch(e => {
            console.log(
              "quizPdfHelper.bulkDownloadQuizAttempts:zip.generateAsync:error:",
              e.message
            );

            return false;
          });
      }
    } else {
      return !!generateQuizAttemptPdf({
        quizAttempts,
        subscriberInfo,
        download: true,
        debug,
      });
    }
  } catch (e) {
    console.log("quizPdfHelper.bulkDownloadQuizAttempts.exception:", e.message);
    return false;
  }
};
